import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import api from "../../services/api";

interface MarketEntry {
  symbol: string;
  name: string;
  price: number;
  change_24h: number;
  volume_24h: number;
  market_cap: number;
  image?: string;
}

interface PriceState {
  prices: Record<string, number>;
  marketData: Record<string, MarketEntry>;
  loading: boolean;
  lastUpdated: string | null;
}

const initialState: PriceState = {
  prices: {},
  marketData: {},
  loading: false,
  lastUpdated: null,
};

// ── Async thunk — REST fallback (used on mount + manual refresh) ──────────────
export const fetchPrices = createAsyncThunk(
  "prices/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/trades/prices");
      return res.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch prices",
      );
    }
  },
);

// ── Slice ─────────────────────────────────────────────────────────────────────
const priceSlice = createSlice({
  name: "prices",
  initialState,
  reducers: {
    /**
     * Merge one or more prices into the map.
     * Called by the WebSocket hook on every incoming tick.
     * e.g. dispatch(setPrices({ BTC: 67432.10 }))
     */
    setPrices(state, action: PayloadAction<Record<string, number>>) {
      Object.assign(state.prices, action.payload);
      state.lastUpdated = new Date().toISOString();
    },

    /**
     * Merge one or more marketData entries.
     * Called by the WebSocket hook after each tick.
     * e.g. dispatch(setMarketData({ BTC: { price, change_24h, ... } }))
     */
    setMarketData(
      state,
      action: PayloadAction<Record<string, Partial<MarketEntry>>>,
    ) {
      for (const [symbol, incoming] of Object.entries(action.payload)) {
        state.marketData[symbol] = {
          ...state.marketData[symbol], // keep existing fields (e.g. market_cap, image from REST)
          ...incoming, // overwrite with live WebSocket data
        } as MarketEntry;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPrices.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPrices.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.prices) {
          Object.assign(state.prices, action.payload.prices);
        }
        if (action.payload.market_data) {
          for (const [symbol, data] of Object.entries(
            action.payload.market_data as Record<string, MarketEntry>,
          )) {
            state.marketData[symbol] = {
              ...state.marketData[symbol],
              ...data,
            };
          }
        }
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchPrices.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { setPrices, setMarketData } = priceSlice.actions;
export default priceSlice.reducer;
